/**
 * Package store using Zustand.
 */

import { create } from 'zustand';

import {
  listPackages,
  getPackage,
  createPackage,
  updatePackage,
  deletePackage,
  getPackageLogs,
} from '@/api/packages';
import type {
  BSPPackage,
  BSPPackageDetail,
  PackageCreate,
  PackageUpdate,
  PackageQueryParams,
  OperationLog,
} from '@/types';

interface PackageState {
  packages: BSPPackage[];
  currentPackage: BSPPackageDetail | null;
  logs: OperationLog[];
  pagination: {
    count: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchPackages: (params?: PackageQueryParams) => Promise<void>;
  fetchPackage: (id: number) => Promise<void>;
  createPackage: (data: PackageCreate) => Promise<BSPPackageDetail>;
  updatePackage: (id: number, data: PackageUpdate) => Promise<BSPPackageDetail>;
  deletePackage: (id: number) => Promise<void>;
  fetchLogs: (packageId: number) => Promise<void>;
  setPackageUploadStatus: (id: number, status: BSPPackage['upload_status']) => void;
  clearCurrentPackage: () => void;
  clearError: () => void;
}

export const usePackageStore = create<PackageState>()((set) => ({
  packages: [],
  currentPackage: null,
  logs: [],
  pagination: {
    count: 0,
    page: 1,
    pageSize: 20,
    totalPages: 0,
  },
  isLoading: false,
  error: null,

  fetchPackages: async (params?: PackageQueryParams) => {
    set({ isLoading: true, error: null });
    try {
      const response = await listPackages(params);
      set({
        packages: response.results,
        pagination: {
          count: response.count,
          page: response.page,
          pageSize: response.page_size,
          totalPages: response.total_pages,
        },
        isLoading: false,
      });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '获取数据失败';
      set({ error: message, isLoading: false });
    }
  },

  fetchPackage: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const pkg = await getPackage(id);
      set({ currentPackage: pkg, isLoading: false });
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '获取详情失败';
      set({ error: message, isLoading: false });
    }
  },

  createPackage: async (data: PackageCreate) => {
    set({ isLoading: true, error: null });
    try {
      const pkg = await createPackage(data);
      set({ isLoading: false });
      return pkg;
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '创建失败';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updatePackage: async (id: number, data: PackageUpdate) => {
    set({ isLoading: true, error: null });
    try {
      const pkg = await updatePackage(id, data);
      set({ currentPackage: pkg, isLoading: false });
      return pkg;
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '更新失败';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deletePackage: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await deletePackage(id);
      set((state) => ({
        packages: state.packages.filter((p) => p.id !== id),
        isLoading: false,
      }));
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        '删除失败';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  fetchLogs: async (packageId: number) => {
    try {
      const logs = await getPackageLogs(packageId);
      set({ logs });
    } catch {
      set({ logs: [] });
    }
  },

  setPackageUploadStatus: (id, status) =>
    set((state) => ({
      packages: state.packages.map((p) => p.id === id ? { ...p, upload_status: status } : p),
    })),

  clearCurrentPackage: () => set({ currentPackage: null, logs: [] }),

  clearError: () => set({ error: null }),
}));
